import { toastController } from '@/controllers/toastController'
import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { storageHelper } from '@/helpers/storageHelper'
import { MAX_ERROR_COUNT, getLoginParams } from '@/pages/LoginPage'
import { userService } from '@/services/userService'
import { Modal } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMount, useUnmount } from 'react-use'
import style from './LoginModal.module.less'

interface LoginModalProps {
  open: boolean
  showLeftBanner?: boolean
  onCancelClick: () => void
  onLoginSuccess: () => void
  giftUserProPlus?: 1 | 0
}

const LoginModal = observer((props: LoginModalProps) => {
  const { open, onCancelClick, onLoginSuccess, giftUserProPlus, showLeftBanner = true } = props

  const [qrCode, setQrCode] = useState('')
  const [loginMethod, setLoginMethod] = useState<'wechat' | 'email'>('wechat')
  const [type, setType] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showRegisterSuccessModal, setShowRegisterSuccessModal] = useState(false)
  const pageUnload = useRef<boolean>(false)
  const checkErrorCount = useRef<number>(0)
  const loginMethodRef = useRef<'wechat' | 'email'>('wechat')
  const checkLoginStatusRef = useRef<((sceneId: string) => Promise<void>) | null>(null)
  const [privacyReadChecked, setPrivacyReadChecked] = useState<boolean>(true)
  useMount(async () => {
    loginMethodRef.current = loginMethod
    if (loginMethod === 'wechat') {
      initLoginQrCode()
    }
  })

  useUnmount(() => {
    pageUnload.current = true
  })

  // 处理登录方式切换
  const handleLoginMethodChange = (method: 'wechat' | 'email') => {
    setLoginMethod(method)
    loginMethodRef.current = method
    // 重置错误计数
    checkErrorCount.current = 0
    // 切换到邮箱登录时，清除二维码
    if (method === 'email') {
      setQrCode('')
    }
    // 切换到微信登录时，重置为登录模式
    if (method === 'wechat') {
      setType('login')
    }
  }

  const checkLoginStatus = useCallback(
    async (sceneId: string) => {
      if (pageUnload.current) {
        return
      }
      // 如果切换到邮箱登录，停止轮询
      if (loginMethodRef.current !== 'wechat') {
        return
      }
      try {
        const { status } = await api.login.checkLoginStatus(sceneId)
        if (status === 'no_scan') {
          await commonUtils.asyncDelay(1 * 1000)
          checkLoginStatus(sceneId)
        } else {
          /** 如果登录用过 inviteCode,则在登录成功后，清除本地的 inviteCode */
          if (storageHelper.get(['inviteCode']).inviteCode) {
            storageHelper.set({ inviteCode: '' })
          }
          // 登录成功 清理 bdUrl/bdVid
          storageHelper.set({ bdUrl: undefined })
          storageHelper.set({ bdVid: undefined })
          // 登录成功 清理 qhclickid
          storageHelper.set({ qhclickid: undefined })
          toastController.success('登录成功')
          await userService.updateUserInfo()
          try {
            commonUtils.sendExtensionMessage('markAuth', {
              isRegister: false,
            })
          } catch (error) {
            //
          }
          onLoginSuccess()
        }
      } catch (error) {
        console.log('error', error)
        // 如果切换到邮箱登录，停止轮询
        if (loginMethodRef.current !== 'wechat') {
          return
        }
        checkErrorCount.current += 1
        if (checkErrorCount.current < MAX_ERROR_COUNT) {
          await commonUtils.asyncDelay(1 * 1000)
          checkLoginStatus(sceneId)
        } else {
          toastController.error('登录状态检测失败，请刷新页面重试或联系客服')
        }
      }
    },
    [onLoginSuccess]
  )

  const initLoginQrCode = useCallback(async () => {
    try {
      const params = getLoginParams()
      const { sceneId, url } = await api.login.getLoginQrCode({ ...params, giftUserProPlus })
      setQrCode(url)
      if (checkLoginStatusRef.current) {
        checkLoginStatusRef.current(sceneId)
      }
    } catch (error) {
      toastController.error('登录二维码获取失败，请稍后重试或联系客服')
    }
  }, [giftUserProPlus])

  // 更新 checkLoginStatusRef
  useEffect(() => {
    checkLoginStatusRef.current = checkLoginStatus
  }, [checkLoginStatus])

  // 当切换回微信登录时，重新初始化二维码
  useEffect(() => {
    if (loginMethod === 'wechat' && !qrCode && open) {
      initLoginQrCode()
    }
  }, [loginMethod, qrCode, open, initLoginQrCode])

  // 验证邮箱格式
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // 验证密码强度（至少6位）
  const validatePassword = (password: string): boolean => {
    return password.length >= 6
  }

  // 邮箱登录处理
  const handleEmailLogin = async () => {
    setEmailError('')
    setPasswordError('')

    if (!email.trim()) {
      setEmailError('请输入邮箱')
      return
    }
    if (!validateEmail(email)) {
      setEmailError('请输入正确的邮箱格式')
      return
    }
    if (!password) {
      setPasswordError('请输入密码')
      return
    }
    if (!validatePassword(password)) {
      setPasswordError('密码至少6位')
      return
    }
    if (!privacyReadChecked) {
      toastController.error('请先阅读并同意用户协议和隐私政策')
      return
    }

    setIsSubmitting(true)
    try {
      await api.login.loginByEmail({ email: email.trim(), password })
      toastController.success('登录成功')

      // 清理本地存储的邀请码等参数（与微信登录保持一致）
      if (storageHelper.get(['inviteCode']).inviteCode) {
        storageHelper.set({ inviteCode: '' })
      }
      storageHelper.set({ bdUrl: undefined })
      storageHelper.set({ bdVid: undefined })
      storageHelper.set({ qhclickid: undefined })

      await userService.updateUserInfo()

      try {
        commonUtils.sendExtensionMessage('markAuth', {
          isRegister: false,
        })
      } catch (error) {
        //
      }

      onLoginSuccess()
    } catch (error) {
      const errorMsg = commonUtils.formatErrorMsg(error)
      toastController.error(errorMsg || '登录失败，请检查邮箱和密码')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 邮箱注册处理
  const handleEmailRegister = async () => {
    setEmailError('')
    setPasswordError('')

    if (!email.trim()) {
      setEmailError('请输入邮箱')
      return
    }
    if (!validateEmail(email)) {
      setEmailError('请输入正确的邮箱格式')
      return
    }
    if (!password) {
      setPasswordError('请输入密码')
      return
    }
    if (!validatePassword(password)) {
      setPasswordError('密码至少6位')
      return
    }
    if (!privacyReadChecked) {
      toastController.error('请先阅读并同意用户协议和隐私政策')
      return
    }

    setIsSubmitting(true)
    try {
      const params = getLoginParams()
      await api.login.registerByEmail({
        ...params,
        email: email.trim(),
        password,
      })

      setShowRegisterSuccessModal(true)

      // 清理本地存储的邀请码等参数
      if (storageHelper.get(['inviteCode']).inviteCode) {
        storageHelper.set({ inviteCode: '' })
      }
      storageHelper.set({ bdUrl: undefined })
      storageHelper.set({ bdVid: undefined })
      storageHelper.set({ qhclickid: undefined })

      await userService.updateUserInfo()

      try {
        commonUtils.sendExtensionMessage('markAuth', {
          isRegister: true,
        })
      } catch (error) {
        //
      }
    } catch (error) {
      const errorMsg = commonUtils.formatErrorMsg(error)
      toastController.error(errorMsg || '注册失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      width={showLeftBanner ? '715px' : '520px'}
      maskClosable={false}
      wrapClassName={classNames(style.loginModal)}
      open={open}
      centered={true}
      mask={true}
      footer={null}
      getContainer={false}
      onCancel={() => onCancelClick()}
    >
      <div className={classNames(style.loginContent)}>
        {showLeftBanner && (
          <img
            className={classNames(style.img)}
            src={commonUtils.getStaticFile('pdf/icon__login-left.png')}
          />
        )}

        <div className={classNames(style.right)}>
          {/* 登录方式切换 */}
          <div className={style.loginMethodSwitch}>
            <button
              className={classNames(style.switchBtn, {
                [style.switchBtnActive]: loginMethod === 'wechat',
              })}
              onClick={() => handleLoginMethodChange('wechat')}
            >
              微信登录
            </button>
            <button
              className={classNames(style.switchBtn, {
                [style.switchBtnActive]: loginMethod === 'email',
              })}
              onClick={() => handleLoginMethodChange('email')}
            >
              邮箱{type === 'login' ? '登录' : '注册'}
            </button>
          </div>

          {loginMethod === 'wechat' ? (
            <>
              <div className={classNames(style.qrWrap)}>
                {qrCode ? (
                  <img className={style.qrCode} src={qrCode} key='qrCode' />
                ) : (
                  <img
                    key='loading'
                    className={style.loadingIcon}
                    src='https://assets.weibanzhushou.com/web/pt-web/loading.0e6e0f46d750.gif'
                  />
                )}
                {!privacyReadChecked && (
                  <>
                    <div className={style.maskBox}>
                      请先阅读并同意
                      <br />
                      服务协议
                    </div>
                  </>
                )}
              </div>
              <p className={classNames(style.hint, style.tips, style.checkHint)}>
                <span
                  className={classNames(style.checkboxBtn, {
                    [style.checkboxActive]: privacyReadChecked,
                  })}
                  onClick={() => {
                    setPrivacyReadChecked(!privacyReadChecked)
                  }}
                >
                  <img
                    className={style.checkNormal}
                    src='https://assets.weibanzhushou.com/web/pt-web/icon__checkbox.a9ea1d637c0a.png'
                  />
                  <img
                    className={style.checkHover}
                    src='https://assets.weibanzhushou.com/web/pt-web/icon__checkbox-hover.cbdad8599d83.png'
                  />
                  <img
                    className={style.checkActive}
                    src='https://assets.weibanzhushou.com/web/pt-web/icon__checkbox-checked.12cdb386111e.png'
                  />
                </span>
                已阅读并同意
                <a
                  href='https://assets.weibanzhushou.com/web/pt-web/%E4%BC%9A%E8%AF%91-%E7%94%A8%E6%88%B7%E6%B3%A8%E5%86%8C%E5%8F%8A%E6%9C%8D%E5%8A%A1%E5%8D%8F%E8%AE%AE.2e0d8664cd55.pdf'
                  target='_blank'
                  rel='noreferrer'
                >
                  《用户协议》
                </a>
                和
                <a
                  href='https://assets.weibanzhushou.com/web/pt-web/%E4%BC%9A%E8%AF%91-%E9%9A%90%E7%A7%81%E4%BF%9D%E6%8A%A4%E6%94%BF%E7%AD%9620240930.pdf'
                  target='_blank'
                  rel='noreferrer'
                >
                  《隐私政策》
                </a>
              </p>
              <p className={classNames(style.info)}>
                微信扫码关注「会译·对照式翻译」公众号，即可登录，无需注册
              </p>
            </>
          ) : (
            <>
              <div className={style.emailForm}>
                <div className={style.formItem}>
                  <input
                    type='email'
                    className={classNames(style.input, {
                      [style.inputError]: emailError,
                    })}
                    placeholder='请输入邮箱'
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setEmailError('')
                    }}
                    disabled={isSubmitting}
                  />
                  {emailError && <p className={style.errorText}>{emailError}</p>}
                </div>
                <div className={style.formItem}>
                  <input
                    type='password'
                    className={classNames(style.input, {
                      [style.inputError]: passwordError,
                    })}
                    placeholder='请输入密码（至少6位）'
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setPasswordError('')
                    }}
                    disabled={isSubmitting}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isSubmitting) {
                        if (type === 'login') {
                          handleEmailLogin()
                        } else {
                          handleEmailRegister()
                        }
                      }
                    }}
                  />
                  {passwordError && <p className={style.errorText}>{passwordError}</p>}
                </div>
                <button
                  className={style.submitBtn}
                  onClick={type === 'login' ? handleEmailLogin : handleEmailRegister}
                  disabled={isSubmitting || !privacyReadChecked}
                >
                  {isSubmitting ? '提交中...' : type === 'login' ? '登录' : '注册'}
                </button>

                {type === 'login' && (
                  <div className={style.registerLink} onClick={() => setType('register')}>
                    注册
                  </div>
                )}
              </div>
              <p className={classNames(style.hint, style.tips, style.checkHint)}>
                <span
                  className={classNames(style.checkboxBtn, {
                    [style.checkboxActive]: privacyReadChecked,
                  })}
                  onClick={() => {
                    setPrivacyReadChecked(!privacyReadChecked)
                  }}
                >
                  <img
                    className={style.checkNormal}
                    src='https://assets.weibanzhushou.com/web/pt-web/icon__checkbox.a9ea1d637c0a.png'
                  />
                  <img
                    className={style.checkHover}
                    src='https://assets.weibanzhushou.com/web/pt-web/icon__checkbox-hover.cbdad8599d83.png'
                  />
                  <img
                    className={style.checkActive}
                    src='https://assets.weibanzhushou.com/web/pt-web/icon__checkbox-checked.12cdb386111e.png'
                  />
                </span>
                已阅读并同意
                <a
                  href='https://assets.weibanzhushou.com/web/pt-web/%E4%BC%9A%E8%AF%91-%E7%94%A8%E6%88%B7%E6%B3%A8%E5%86%8C%E5%8F%8A%E6%9C%8D%E5%8A%A1%E5%8D%8F%E8%AE%AE.2e0d8664cd55.pdf'
                  target='_blank'
                  rel='noreferrer'
                >
                  《用户协议》
                </a>
                和
                <a
                  href='https://assets.weibanzhushou.com/web/pt-web/%E4%BC%9A%E8%AF%91-%E9%9A%90%E7%A7%81%E4%BF%9D%E6%8A%A4%E6%94%BF%E7%AD%9620240930.pdf'
                  target='_blank'
                  rel='noreferrer'
                >
                  《隐私政策》
                </a>
              </p>
            </>
          )}
        </div>
      </div>
      {/* 注册成功弹窗 */}
      <Modal
        open={showRegisterSuccessModal}
        title='注册申请提交成功'
        onOk={() => {
          setShowRegisterSuccessModal(false)
          setType('login')
        }}
        onCancel={() => {
          setShowRegisterSuccessModal(false)
          setType('login')
        }}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
        okText='我知道了'
      >
        <p>注册认证邮件已发送至您的邮箱，请查收并点击邮件中认证链接完成注册。</p>
      </Modal>
    </Modal>
  )
})

export default LoginModal
